/* The notices grid both Notifications screens show: a row per notice type from the server,
   a Bell and an Email column, and a cell only where the type is sent on that channel.
   Each screen decides what a cell holds. Loading and failure are here too, so the two cannot drift. */
import type { ReactNode } from "react";
import type { UseQueryResult } from "@tanstack/react-query";
import { Bell, Mail } from "lucide-react";

import type { Channel, NotificationSettings, NotificationType, SettingCell } from "@/shared/api/notifications";
import { GhostButton, Group, RowList } from "@/shared/components/settings-atoms";
import { Skeleton } from "@/shared/components/ui/skeleton";

const COLUMN_WIDTH = "w-[210px]";
const LOADING_ROWS = [0, 1, 2];

const CHANNELS: { id: Channel; label: string; icon: typeof Bell }[] = [
  { id: "inbox", label: "Bell", icon: Bell },
  { id: "email", label: "Email", icon: Mail },
];

interface Props {
  types: UseQueryResult<NotificationType[]>;
  settings: UseQueryResult<NotificationSettings>;
  /** Another settings read the screen needs before it can show the grid. */
  extra?: UseQueryResult<NotificationSettings>;
  /** Which types this screen lists. */
  include?: (type: NotificationType) => boolean;
  /** The control for one cell; `name` labels it for screen readers. */
  renderCell: (cell: SettingCell, name: string) => ReactNode;
  footer?: ReactNode;
  emptyText?: string;
  disabled?: boolean;
}

export default function NotificationGrid({ types, settings, extra, include = () => true, renderCell, footer, emptyText, disabled }: Props) {
  const failed = types.error ?? settings.error ?? extra?.error;
  if (failed) {
    return (
      <div role="alert" className="flex items-center justify-between gap-3 py-3 text-[13px] text-[var(--text-tertiary)]">
        {failed.message}
        <GhostButton onClick={() => { types.refetch(); settings.refetch(); extra?.refetch(); }}>Retry</GhostButton>
      </div>
    );
  }
  if (!types.data || !settings.data || (extra && !extra.data)) {
    return (
      <div aria-busy="true" className="flex flex-col gap-3">
        {LOADING_ROWS.map((i) => <Skeleton key={i} className="h-12 rounded-[14px]" />)}
      </div>
    );
  }

  const rows = types.data.filter(include);
  const cells = settings.data.settings;
  const columns = CHANNELS.filter(({ id }) => rows.some((type) => id in type.defaults));

  return (
    <fieldset disabled={disabled} className="m-0 min-w-0 border-0 p-0">
      <Group>
        <RowList
          header={
            <>
              <span className="flex-1">Activity</span>
              {columns.map(({ id, label, icon: Icon }) => (
                <span key={id} className={`${COLUMN_WIDTH} hidden xl:flex items-center justify-center gap-1`}>
                  <Icon className="w-3 h-3" strokeWidth={1.75} /> {label}
                </span>
              ))}
            </>
          }
          footer={footer}
        >
          {rows.length === 0 && emptyText && (
            <p className="m-0 py-3.5 text-[11.5px] italic text-[var(--text-quaternary)]">{emptyText}</p>
          )}
          {rows.map((type) => (
            <div key={type.id} className="flex flex-col gap-2.5 xl:flex-row xl:items-start xl:gap-4 py-3.5">
              <div className="flex-1 min-w-0 xl:pt-1">
                <div className="text-[13.5px] font-medium text-[var(--text-primary)]">{type.label}</div>
                <p className="m-0 mt-0.5 text-[12px] leading-[1.5] text-[var(--text-tertiary)]">{type.description}</p>
              </div>
              <div className="flex flex-wrap gap-x-4 gap-y-3 xl:contents">
                {columns.map(({ id, label }) => {
                  const cell = cells.find((c) => c.type === type.id && c.channel === id);
                  const name = `${type.label}, ${label}`;
                  return (
                    <div key={id} className={`${COLUMN_WIDTH} flex flex-col items-center gap-1 max-xl:w-auto max-xl:items-start ${cell ? "" : "xl:pt-1"}`}>
                      {/* Below xl the settings rail leaves too little room for the full grid. */}
                      <span className="xl:hidden text-[11.5px] text-[var(--text-quaternary)]">{label}</span>
                      {cell ? renderCell(cell, name) : (
                        <span role="img" aria-label={`${name}, not sent`} className="text-[var(--text-quaternary)]">—</span>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </RowList>
      </Group>
    </fieldset>
  );
}
